import {
  IRawTemplate, IReportConfig, IReportMetaInfo, IReportMetaInfoV2,
} from 'core/models/report-builder/report-builder';
import { ObjModel, ReportResponseModel } from 'core/models';
import { DynamicMeasureResponse, IColumn, IField } from 'core/models/report-response';
import { defaultPreviewPaging } from 'components/feature/Report/ReportSidebar/common/constants';
import SieraResponse from 'core/models/siera-response';
import { IEditReportProfilePayload } from 'redux-v2/global-store/global-store.state';
import { SaveTempDrilldownCriteria } from 'core/models/dashboard';
import {
  DrilldownColumnConfig,
  DrilldownColumnConfigResponse,
  IDataSourceApiResponse,
  IDataSourceSavePayload,
  ILinkedConfig,
  MeasureIdentifier,
  MeasureIdentifierPayload,
  UpdateType,
} from 'redux-v2/report-builder/report-builder-store.state';
import { FeatureContext, PageContext } from 'core/constants/common';
import API from './api';

export default class ReportBuilderService extends API {
  static baseURL: string = process.env.REACT_APP_DATA_BASE_URL || '';

  constructor() {
    super(ReportBuilderService.baseURL, true, true);
  }

   saveReportConfig = async (data: IReportConfig, queryParams?: ObjModel.Obj) : Promise<SieraResponse<string>> => this.genericResponseWrapper(
     await this.instance.post('reports/save-report-config', data, {
       params: queryParams,
     }),
   );

  getDynamicFilters = async (
    reportId: string,
  ): Promise<Array<ReportResponseModel.IFilterConfig>> => this.responsify(
    await this.instance.get('visualisation/get-additional-filters', {
      params: {
        reportId,
      },
    }),
  );

  getDynamicDimensions = async (
    reportId: string,
  ): Promise<Array<ReportResponseModel.IColumn>> => this.responsify(
    await this.instance.get('visualisation/get-additional-dimensions', {
      params: {
        reportId,
      },
    }),
  );

  getDynamicMeasures = async (
    reportId: string,
  ): Promise<Array<DynamicMeasureResponse>> => this.responsify(
    await this.instance.get('visualisation/get-additional-measures', {
      params: {
        reportId,
      },
    }),
  );

   getFieldsRequest = async (
     reportId: string,
   ): Promise<Array<IField>> => this.responsify(
     await this.instance.get('visualisation/get-fields', {
       params: {
         reportId,
       },
     }),
   );

   getDynamicFiltersForNamespaces = async (
     reportMetaInfo: IReportMetaInfoV2,
   ): Promise<Array<ReportResponseModel.IFilterConfig>> => this.responsify(
     await this.instance.post('visualisation/filters', reportMetaInfo),
   );

  getDynamicDimensionsForNamespaces = async (
    reportMetaInfo: IReportMetaInfoV2,
  ): Promise<Array<ReportResponseModel.IColumn>> => this.responsify(
    await this.instance.post('visualisation/dimensions', reportMetaInfo),
  );

  getDynamicMeasuresForNamespaces = async (
    reportMetaInfo: IReportMetaInfoV2,
  ): Promise<Array<DynamicMeasureResponse>> => this.responsify(
    await this.instance.post('visualisation/measures', reportMetaInfo),
  );

   getFieldsRequestForNamespaces = async (
     reportMetaInfo: IReportMetaInfoV2,
   ): Promise<Array<IField>> => this.responsify(
     await this.instance.post('visualisation/fields', reportMetaInfo),
   );

 getAllTags = async () => (
   this.responsify(await this.instance.get('unified-listing/tags/list'))
 );

  getDynamicDataById= async (
    reportId: string,
    featureContext: FeatureContext,
  ) => this.responsify(
    await this.instance.post('reports/fetch-report-v2', {
      DataSourceId: reportId,
      DataSourceType: 'Report',
      FeatureContext: featureContext,
      Page: {
        ...defaultPreviewPaging,
      },
    }),
  );

  getProfileList = async () => this.responsify(await this.instance.get('profiles/list'))

  editProfilesToReport = async (data: IEditReportProfilePayload) => (
    this.responsify(await this.instance.put('profiles/update-report-profiles', data))
  )

  getReportRawConfig = async (params: ObjModel.Obj): Promise<SieraResponse<IReportConfig>> => (
    this.genericResponseWrapper(await this.instance.get('reports/get-raw-config', { params }))
  );

  getReportRenderConfig = async (params: ObjModel.Obj): Promise<SieraResponse<ReportResponseModel.IReportConfig>> => (
    this.genericResponseWrapper(await this.instance.post('reports/get-report-config', null, { params }))
  )

  getDefaultDrilldownColumns = async (reportId: string, data: MeasureIdentifierPayload) : Promise<Array<DrilldownColumnConfigResponse>> => this.responsify(await this.instance.post('visualisation/get-default-drilldown-columns', data, {
    params: {
      reportId,
    },
  }))
  ;

  getAdditionalDrilldownColumns = async (reportId: string, data: MeasureIdentifier) : Promise<Array<ReportResponseModel.IColumn>> => (
    this.responsify(await this.instance.post('visualisation/get-additional-drilldown-columns-by-identifier', data, {
      params: {
        reportId,
      },
    }))
  )

  getAdditionalListReportColumns = async (reportId: string) : Promise<Array<ReportResponseModel.IColumn>> => (
    this.responsify(await this.instance.get('visualisation/get-additional-columns', {
      params: {
        reportId,
      },
    }))
  )

  getAdditionalListReportColumnsForNamespaces = async (reportMetaInfo: IReportMetaInfoV2) : Promise<Array<ReportResponseModel.IColumn>> => (
    this.responsify(await this.instance.post('visualisation/columns', reportMetaInfo))
  )

  getDrilldownConfig = async (reportId: string): Promise<SieraResponse<any>> => (
    this.genericResponseWrapper(await this.instance.get('reports/fetch-drilldown-config', { params: { reportId } }))

  )

  saveDrilldownColumnConfig = async (reportId: string, data: DrilldownColumnConfig): Promise<SieraResponse<any>> => (
    this.responsify(await this.instance.post('reports/save-drilldown-config', data, { params: { reportId } }))
  )

  getRawTemplateDetails = async (namespace:string) => this.responsify(await this.instance.get('reports/get-raw-template', {
    params: {
      namespace,
    },
  }));

  getDataSourcesList = async (reportId?: string, pageContext?: PageContext): Promise<IDataSourceApiResponse[]> => this.responsify(await this.instance.get('visualisation/get-data-sources', {
    params: {
      ReportId: reportId,
      PageContext: pageContext,
    },
  }));

  saveDataSourceChanges = async (reportId: string, data: IDataSourceSavePayload, updateType?: UpdateType) => (
    this.responsify(await this.instance.post('visualisation/apply-report-joins', data, { params: { reportId, UpdateType: updateType ?? UpdateType.CustomColumns } }))
  )

  saveNestedConfigChanges = async (pageContext: PageContext, data: IReportMetaInfo, updateType?: UpdateType): Promise<IRawTemplate> => (
    this.responsify(await this.instance.post('visualisation/query', data, {
      params: {
        PageContext: pageContext,
        UpdateType: updateType ?? UpdateType.CustomColumns,
      },
    }))
  )

  getMetaSelectorOptions = async (params: ObjModel.Obj) : Promise<Array<any>> => (
    this.responsify(await this.instance.post('visualisation/get-metafield-selector-options', { ...params }))
  );

  getDataSourcesFieldList = async (dataSourceId : string, reportId?: string, pageContext?: PageContext): Promise<IColumn[]> => this.responsify(await this.instance.get('visualisation/data-source/join-fields', {
    params: {
      dataSourceId,
      ReportId: reportId,
      PageContext: pageContext,
    },
  }));

  saveTempDrilldownState = async (criteria: SaveTempDrilldownCriteria): Promise<{
    TempReportId: string;
    LinkedReportId?: string;
    LinkedReportsConfig?: ILinkedConfig[];
  }> => this.responsify(
    await this.instance.post('reports/save-temp-drilldown-state', criteria),
  );
}
